import React, {useContext, useMemo, useState} from 'react';
import {AppContext} from '../App';
import {Button, Table} from 'react-bootstrap';
import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import {useNavigate, useParams, useSearchParams, useNavigationType} from 'react-router-dom';
import {Screen} from '../types';
import {apiService} from '../services/api';
import {ToastNotification} from './ToastNotification';
import {EditableTableCell} from './EditableTableCell';
import {ConfirmModal} from './ConfirmModal';
import {WorkflowModal} from './WorkflowModal';
import {SelectModal} from './SelectModal';
import {useCellEdit, useToast, useScreenNotifications, useColumnWidths, useIncomingNotice} from '../hooks';
import {useFilters} from '../contexts';
import {useMercure, MercureEvent} from '../hooks/useMercure';
import './ScreenDataTable.css';
import {IconRefresh, IconArrowUp, IconArrowDown, IconArrowsSort, IconColumnsOff} from "@tabler/icons-react";
import {FilterGroup} from './FilterGroup';
import {ScreenDataTableFilters} from './ScreenDataTableFilters';
import {ScreenDataTablePagination} from './ScreenDataTablePagination';
import {ScreenDataTableActions} from './ScreenDataTableActions';
import {ScreenDataTableHeader} from './ScreenDataTableHeader';
import {FormModal} from './FormModal';
import {normalizeConditions, buildFiltersFromGroup} from '../utils/filterUtils';
import {applyRowContextFilters, screenRequestFilters} from '../utils/contextFilters';
import {resolveAssignShape, AssignShape} from '../utils/oneToManyAssign';
import {buildContextFields} from '../utils/formInitialization';
import {applyWorkflowRedirect, readWorkflowMessage, readWorkflowRedirect} from '../utils/workflowResult';
import {storage} from '../utils/localStorage';
import {downloadFile, FileData} from '../utils/fileUtils';
import {expandColumns, ExpandedColumn, hasBarcodeSearch, hasSearchableColumn} from '../utils/columnExpander';
import {decodeJWT} from '../utils/jwt';
import {commentPart, isCommentsScreen, isTitleColumn, resolveAppearance} from '../utils/uiUtils';
import {CommentComposer, commentTextField} from './CommentComposer';
import {searchPrefill, searchPrefillFieldCode} from '../utils/searchPrefill';
import {ScreenSort, sortOfScreen, toggleScreenSort} from '../utils/screenSort';

interface ScreenDataTableProps {
    screen: Screen;
    filters?: Record<string, any>;
    onNavigateToScreen?: (screenId: string, titleValue?: string, previousFilters?: Record<string, string>) => void;
    refreshTrigger?: number;
    onDataChange?: () => void;
    onOpenUpdateForm?: (form: any, rowData: any) => void;
    onOpenCreateForm?: (form: any, prefill?: Record<string, any>) => void;
    onRowClick?: (row: any) => void;
    selectionMode?: boolean;
    /** Ids currently picked in a multi-select SelectModal, marked in the table. */
    selectedRowIds?: (string | number)[];
    /** Ids that are attached but cannot be detached - marked apart from picks. */
    lockedRowIds?: (string | number)[];
    embedded?: boolean;
    /** Only the table (a home page widget): no controls row, filters or pagination. */
    displayOnly?: boolean;
    editable?: boolean;
    embeddedTitle?: string;
    parentFormId?: string;
    parentRecordId?: string | number;
}

export const ScreenDataTable: React.FC<ScreenDataTableProps> = ({
                                                                    screen,
                                                                    filters,
                                                                    onNavigateToScreen,
                                                                    refreshTrigger,
                                                                    onOpenUpdateForm,
                                                                    onOpenCreateForm,
                                                                    onRowClick,
                                                                    selectionMode = false,
                                                                    selectedRowIds,
                                                                    lockedRowIds,
                                                                    embedded = false,
                                                                    displayOnly = false,
                                                                    editable = true,
                                                                    embeddedTitle,
                                                                    parentFormId,
                                                                    parentRecordId
                                                                }) => {
    const [params, setParams] = useSearchParams();
    const navigationType = useNavigationType();

    // Load search from localStorage for this specific screen
    const searchKey = `search_${screen.id}`;
    const [search, setSearch] = useState(() => {
        if (embedded) return ""; // Embedded mode never uses persisted search

        // Only load saved search if navigating back (browser back button)
        if (navigationType === 'POP') {
            const saved = storage.getItem(searchKey);
            return saved || "";
        }

        return "";
    });

    // Update search when screen changes (only on actual screen change, not navigation type change)
    const prevScreenId = React.useRef(screen.id);
    React.useEffect(() => {
        if (prevScreenId.current !== screen.id) {
            prevScreenId.current = screen.id;
            if (!embedded) {
                const newSearchKey = `search_${screen.id}`;

                if (navigationType === 'POP') {
                    const saved = storage.getItem(newSearchKey);
                    setSearch(saved || "");
                } else {
                    setSearch("");
                    storage.removeItem(newSearchKey);
                }
            }
        }
    }, [screen.id, embedded, navigationType]);

    const [showUpdateDropdown, setShowUpdateDropdown] = useState<string | number | null>(null);
    const [showCreateDropdown, setShowCreateDropdown] = useState(false);
    const [showSavedFiltersDropdown, setShowSavedFiltersDropdown] = useState(false);
    const [deletingRowId, setDeletingRowId] = useState<string | number | null>(null);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [rowToDelete, setRowToDelete] = useState<any>(null);
    const [showFilters, setShowFilters] = useState(false);
    const [filterGroup, setFilterGroup] = useState<FilterGroup>({
        type: 'group',
        operator: 'AND',
        conditions: []
    });
    const selectedUserFilterFromUrl = params.get("userFilter");
    const [selectedUserFilter, setSelectedUserFilterState] = useState<string | null>(selectedUserFilterFromUrl);
    const [originalFilterGroup, setOriginalFilterGroup] = useState<FilterGroup | null>(null);

    // Get current user ID for identifying own comments
    const currentUserId = React.useMemo(() => {
        const token = storage.getItem('token');
        if (token) {
            const payload = decodeJWT(token);
            console.log('Current user from JWT:', payload?.user?.id);
            return payload?.user?.id ?? null;
        }
        return null;
    }, []);
    const [appliedFilterGroup, setAppliedFilterGroup] = useState<FilterGroup | null>(null);
    const [showWorkflowModal, setShowWorkflowModal] = useState(false);
    const [workflowLoading, setWorkflowLoading] = useState(false);
    const [workflowSuccess, setWorkflowSuccess] = useState<boolean | undefined>(undefined);
    const [workflowMessage, setWorkflowMessage] = useState<string | undefined>(undefined);
    const [workflowTitle, setWorkflowTitle] = useState<string>('');
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmConfig, setConfirmConfig] = useState<{title: string, body: string, confirmButtonText: string, onConfirm: () => void, variant?: 'danger' | 'warning' | 'primary'} | null>(null);
    // Load afterId from URL params (screen-specific keys for multi-table pages)
    const afterIdKey = `afterId_${screen.id}`;
    const historyKey = `afterIdHistory_${screen.id}`;
    const afterIdFromUrl = params.get(afterIdKey);
    const historyFromUrl = params.get(historyKey);

    const [afterId, setAfterIdState] = useState<number | null>(() => {
        if (embedded) return null;
        return afterIdFromUrl ? Number(afterIdFromUrl) : null;
    });
    const [afterIdHistory, setAfterIdHistoryState] = useState<(number | null)[]>(() => {
        if (embedded) return [];
        if (historyFromUrl) {
            try {
                return JSON.parse(historyFromUrl);
            } catch { return []; }
        }
        return [];
    });
    const [pageSize, setPageSize] = useState(() => {
        const defaultPage = screen.pages?.find(p => p.default);
        return defaultPage?.limit || 100;
    });

    // Combined function to update both afterId and history together (avoids race condition)
    const updatePagination = (newAfterId: number | null, newHistory: (number | null)[]) => {
        setAfterIdState(newAfterId);
        setAfterIdHistoryState(newHistory);
        if (!embedded) {
            // Functional update - a stale `params` closure here would drop
            // params written moments ago (e.g. userFilter set right before
            // the filter-change effect resets pagination).
            setParams(prev => {
                const newParams = new URLSearchParams(prev);
                if (newAfterId !== null) {
                    newParams.set(afterIdKey, String(newAfterId));
                } else {
                    newParams.delete(afterIdKey);
                }
                if (newHistory.length > 0) {
                    newParams.set(historyKey, JSON.stringify(newHistory));
                } else {
                    newParams.delete(historyKey);
                }
                return newParams;
            }, { replace: true });
        }
    };

    // Sort key = dot-joined dataPath of the expanded column ("name",
    // "project_owner.department.name"). Column id can't be used: expanded
    // sub-columns keep the innermost column's id/code, so two different
    // paths may collide (and the bare code collides with base columns).
    const [sortState, setSortState] = useState<ScreenSort | null>(null);
    const sort = sortOfScreen(sortState, screen.id);
    const sortColumnId = sort?.columnId ?? null;
    const sortDir = sort?.direction ?? null;

    const handleSort = (columnId: string) => {
        setSortState(toggleScreenSort(sortState, screen.id, columnId));
        updatePagination(null, []);
    };

    const setSelectedUserFilter = (filterId: string | null) => {
        setSelectedUserFilterState(filterId);
        // Reset pagination in the SAME navigation. Two setParams calls in one
        // commit don't compose - the router's functional updater reads params
        // from the last render, so a separate pagination reset would overwrite
        // the userFilter param just written here.
        setAfterIdState(null);
        setAfterIdHistoryState([]);
        setParams(prev => {
            const newParams = new URLSearchParams(prev);
            if (filterId) {
                newParams.set('userFilter', filterId);
            } else {
                newParams.delete('userFilter');
            }
            newParams.delete(afterIdKey);
            newParams.delete(historyKey);
            return newParams;
        }, {replace: true});
    };

    const queryClient = useQueryClient();
    const {toasts, showToast, showSuccess, showError, hideToast} = useToast();
    useIncomingNotice(showToast);
    const {widths, startResize, resetWidths} = useColumnWidths(screen.id);
    const {getFilter, setFilter, removeFilter} = useFilters();

    // Set of screen IDs in the current app — used to scope broadcast
    // invalidate after edits so we only refetch cached queries for the
    // current app, not every screen from every previously visited project.
    const {appId: routeAppId} = useParams<{appId: string}>();
    const {applications} = useContext(AppContext);
    const navigate = useNavigate();
    const currentApp = useMemo(
        () => applications?.find((a: any) => String(a.id) === routeAppId) ?? null,
        [applications, routeAppId],
    );
    const currentAppScreenIds = useMemo(() => {
        const app = applications?.find((a: any) => String(a.id) === routeAppId);
        return new Set<string>(app?.screens?.map((s: any) => String(s.id)) ?? []);
    }, [applications, routeAppId]);

    const invalidateAppScreens = React.useCallback(() => {
        queryClient.invalidateQueries({
            predicate: (q) =>
                q.queryKey[0] === 'screenData'
                && currentAppScreenIds.has(String(q.queryKey[1])),
            refetchType: 'all',
        });
    }, [queryClient, currentAppScreenIds]);

    const {editValue, setEditValue, handleCellClick, handleCellBlur, handleDirectUpdate, isEditing} = useCellEdit(showSuccess, showError, currentAppScreenIds);

    // Screen notifications
    const {
        notifications,
        unreadCount,
        addNotification,
        markAllAsRead,
        dismissNotification,
        clearAll
    } = useScreenNotifications(screen.id);

    // Mercure real-time updates - highlighted rows
    const [highlightedRows, setHighlightedRows] = useState<Set<number | string>>(new Set());

    // Mercure real-time updates
    const getRecordDisplayText = React.useCallback((record: any): string => {
        if (!record?.data) {
            return `ID ${record?.id || 'neznáme'}`;
        }

        // Pokús sa nájsť vhodný display field
        const displayFields = ['name', 'title', 'displayName', 'email', 'firstName', 'lastName', 'code'];

        for (const field of displayFields) {
            if (record.data[field]) {
                return record.data[field];
            }
        }

        // Ak má firstName a lastName, spoj ich
        if (record.data.firstName || record.data.lastName) {
            return `${record.data.firstName || ''} ${record.data.lastName || ''}`.trim();
        }

        // Fallback na ID
        return `ID ${record.id}`;
    }, []);

    const handleMercureEvent = React.useCallback((event: MercureEvent) => {
        // Invalidate query cache to refresh data
        queryClient.invalidateQueries({
            queryKey: ["screenData", screen.id]
        });

        const recordText = getRecordDisplayText(event.record);

        // Check if current user made the change
        let isCurrentUser = false;
        if (event.user) {
            const currentUserData = storage.getItem('user');
            if (currentUserData) {
                try {
                    const userData = JSON.parse(currentUserData);
                    const currentUserName = `${userData.firstName || ''} ${userData.lastName || ''}`.trim();
                    isCurrentUser = event.user === currentUserName;
                } catch (error) {
                    isCurrentUser = false;
                }
            }
        }

        // Zvýrazni riadok na 3 sekundy (okrem deleted)
        if (event.action !== 'deleted' && event.record?.id) {
            setHighlightedRows(prev => new Set(prev).add(event.record.id));

            setTimeout(() => {
                setHighlightedRows(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(event.record.id);
                    return newSet;
                });
            }, 3000);
        }

        // Handle notifications based on event type
        // Only show notifications if someone else made the change
        if (!isCurrentUser) {
            if (event.action === 'created') {
                // Created: Add to notifications only
                addNotification('created', event.record.id, recordText, event.user);
            } else if (event.action === 'updated') {
                // Updated: Add to notifications only
                addNotification('updated', event.record.id, recordText, event.user);
            } else if (event.action === 'deleted') {
                // Deleted: Add to notifications only (deleted items won't be highlighted)
                addNotification('deleted', event.record.id, recordText, event.user);
            }
        }
    }, [queryClient, screen.id, getRecordDisplayText, addNotification]);

    useMercure({
        screenId: screen.id,
        enabled: !embedded && (screen.notification === true), // Vypnúť v embedded mode alebo ak notification nie je true
        onEvent: handleMercureEvent
    });

    const buildFilters = (): { filters: Record<string, any>, filterGroups: any } => {
        // Filters from the global context: the screen's own, then its screen filters' related screens
        const builtFilters = screenRequestFilters(screen, getFilter);

        // Then apply explicit filters prop (takes priority over context)
        if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                builtFilters[key] = value;
            });
        }

        return {
            filters: builtFilters,
            // Use ONLY appliedFilterGroup — builder state (filterGroup) is in-progress
            // edits and should NOT filter the request until user clicks "Filtrovať" or
            // selects a saved filter (both call setAppliedFilterGroup).
            filterGroups: buildFiltersFromGroup(appliedFilterGroup)
        };
    };

    const {filters: filtersToUse, filterGroups} = buildFilters();
    const explicitFilterValues = filters ? Object.entries(filters).map(([k, v]) => `${k}=${v}`).join(',') : '';
    const collectScreenFilterValues = (screenData: any, depth = 0): string[] => {
        if (depth > 10 || !screenData?.screenFilters?.length) return [];
        const values: string[] = [];
        screenData.screenFilters.forEach((sf: any) => {
            const col = screenData.columns?.find((c: any) => c.id === sf.screenColumnId);
            if (col?.relatedScreen) {
                col.relatedScreen.filters?.forEach((f: any) => values.push(getFilter(f.code) || ''));
                (col.relatedScreen.queryParameters || []).forEach((key: string) => values.push(getFilter(key) || ''));
                values.push(...collectScreenFilterValues(col.relatedScreen, depth + 1));
            }
        });
        return values;
    };
    const screenFilterValues = collectScreenFilterValues(screen).join(',');
    const contextFilterValues = [
        screen.filters?.map(f => getFilter(f.code)).join(',') || '',
        screenFilterValues
    ].filter(Boolean).join(',');
    const relevantFilterValues = explicitFilterValues || contextFilterValues;

    // Track if this is the first render (to skip resetting pagination on mount)
    const isFirstRender = React.useRef(true);
    const prevSearch = React.useRef(search);
    const prevFilterValues = React.useRef(relevantFilterValues);
    const prevUserFilter = React.useRef(selectedUserFilter);

    // Reset pagination when search changes (but not on first render)
    React.useEffect(() => {
        if (isFirstRender.current) {
            return;
        }
        if (prevSearch.current !== search) {
            updatePagination(null, []);
            prevSearch.current = search;
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    // Reset pagination when filters change (but not on first render)
    React.useEffect(() => {
        if (isFirstRender.current) {
            return;
        }
        if (prevFilterValues.current !== relevantFilterValues || prevUserFilter.current !== selectedUserFilter) {
            // Only touch the URL when there is pagination state to clear.
            // setSelectedUserFilter already resets pagination in its own
            // navigation; an unconditional write here would run with stale
            // params and drop the freshly written userFilter.
            if (afterId !== null || afterIdHistory.length > 0) {
                updatePagination(null, []);
            }
            prevFilterValues.current = relevantFilterValues;
            prevUserFilter.current = selectedUserFilter;
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [relevantFilterValues, selectedUserFilter]);

    // Mark first render as complete after all effects run
    React.useEffect(() => {
        isFirstRender.current = false;
    }, []);

    // Load saved filters
    const {data: savedFilters = []} = useQuery({
        queryKey: ["userFilters", screen.id],
        queryFn: async () => {
            const response = await apiService.getUserFilters(screen.id);
            const data: any = response.data;
            return response.success ? (data?.data || data || []) : [];
        }
    });

    // Load filter from URL on mount, or fall back to user's default for this screen.
    // Runs ONCE — after that the user can freely clear/select filters without auto-reapplying.
    const initialFilterAppliedRef = React.useRef(false);
    React.useEffect(() => {
        if (initialFilterAppliedRef.current) return;
        if (savedFilters.length === 0) return;

        // 1) URL-pinned filter takes precedence
        if (selectedUserFilterFromUrl) {
            const filter = savedFilters.find((f: any) => f.id === selectedUserFilterFromUrl);
            if (filter && filter.conditions) {
                const normalized = normalizeConditions(filter.conditions);
                setFilterGroup(normalized);
                setOriginalFilterGroup(normalized);
                setAppliedFilterGroup(normalized);
                setSelectedUserFilterState(selectedUserFilterFromUrl);
                setShowFilters(false);
                initialFilterAppliedRef.current = true;
                return;
            }
        }

        // 2) User's default filter for this screen (if any)
        const def = savedFilters.find((f: any) => f.isDefault);
        if (def && def.conditions) {
            const normalized = normalizeConditions(def.conditions);
            setFilterGroup(normalized);
            setOriginalFilterGroup(normalized);
            setAppliedFilterGroup(normalized);
            setSelectedUserFilterState(def.id);
        }
        initialFilterAppliedRef.current = true;
    }, [selectedUserFilterFromUrl, savedFilters]);

    // Get filter operators from localStorage (loaded with applications)
    const allOperators = React.useMemo(() => {
        const stored = storage.getItem('filterOperators');
        return stored ? JSON.parse(stored) : {};
    }, []);

    const sortParam: Record<string, 'ASC' | 'DESC'> | undefined = React.useMemo(() => {
        if (!sortColumnId || !sortDir) return undefined;
        return { [sortColumnId]: sortDir };
    }, [sortColumnId, sortDir]);

    // If the applied filter has diverged from the saved filter (user modified locally),
    // send filter_groups instead of userFilter so backend honors the modifications.
    const appliedDiffersFromSaved = !!selectedUserFilter
        && JSON.stringify(appliedFilterGroup) !== JSON.stringify(originalFilterGroup);
    const effectiveUserFilterId = appliedDiffersFromSaved ? null : selectedUserFilter;

    const {data: queryData, isLoading, isFetching, isPlaceholderData, error} = useQuery({
        queryKey: ["screenData", screen.id, search, relevantFilterValues, effectiveUserFilterId, refreshTrigger, embedded ? "modal" : "normal", afterId, pageSize, sortColumnId, sortDir, JSON.stringify(appliedFilterGroup)],
        queryFn: async ({signal}) => {
            const response = await apiService.getScreenData(
                screen.id,
                filtersToUse,
                search || undefined,
                filterGroups,
                effectiveUserFilterId,
                afterId,
                pageSize,
                signal,
                sortParam
            );

            // Check if request was aborted
            if (signal?.aborted) {
                throw new Error('Request aborted');
            }

            if (!response.success) {
                throw new Error(response.message || 'Failed to load data');
            }

            // Handle paginated response
            const responseData = response.data;
            if (responseData && typeof responseData === 'object' && 'data' in responseData) {
                const dataArray = Array.isArray(responseData.data) ? responseData.data : [];
                return {
                    data: dataArray,
                    totalRecords: responseData.total || 0,
                    hasMore: dataArray.length === pageSize
                };
            }

            const dataArray = Array.isArray(responseData) ? responseData : [];
            return {
                data: dataArray,
                totalRecords: dataArray.length,
                hasMore: dataArray.length === pageSize
            };
        },
        gcTime: embedded ? 0 : undefined,
        placeholderData: (previousData) => previousData,
        // Always refetch when the table mounts. Without this, navigating back
        // from a child screen (where a nested edit invalidated the cache) can
        // show stale parent data — placeholderData masks the stale state and
        // React Query doesn't auto-refetch with the global 5-min staleTime.
        refetchOnMount: 'always',
    });

    const data = queryData?.data || [];
    const totalRecords = queryData?.totalRecords || 0;
    const hasMore = queryData?.hasMore || false;

    // Cursor-based pagination handlers
    const handleNextPage = () => {
        if (data.length > 0 && hasMore) {
            const lastId = data[data.length - 1]?.id;
            if (lastId !== undefined) {
                const newHistory = [...afterIdHistory, afterId];
                updatePagination(lastId, newHistory);
            }
        }
    };

    const handlePrevPage = () => {
        if (afterIdHistory.length > 0) {
            const newHistory = [...afterIdHistory];
            const prevAfterId = newHistory.pop();
            updatePagination(prevAfterId ?? null, newHistory);
        }
    };

    const canGoPrev = afterIdHistory.length > 0;
    const canGoNext = hasMore;

    const deleteMutation = useMutation({
        mutationFn: (rowId: string | number) => apiService.deleteScreenData(screen.id, rowId)
    });

    const handleOneToManyDelete = (column: any, childId: number | string) => {
        const relatedScreenId = column.relatedScreen?.id;
        if (!relatedScreenId) return;
        setConfirmConfig({
            title: 'Odstrániť záznam',
            body: 'Naozaj chcete odstrániť tento záznam?',
            confirmButtonText: 'Odstrániť',
            variant: 'danger',
            onConfirm: async () => {
                try {
                    await apiService.deleteScreenData(relatedScreenId, childId);
                    // Refresh cached parent screens of the current app
                    // (background refetch even if they're currently unmounted)
                    // so back-navigation shows fresh data without a blink.
                    invalidateAppScreens();
                    showSuccess('Záznam bol odstránený');
                } catch (error: any) {
                    showError(error?.message || 'Nepodarilo sa odstrániť záznam');
                }
            },
        });
        setShowConfirmModal(true);
    };

    const [oneToManyAssignState, setOneToManyAssignState] = useState<{
        shape: AssignShape;
        assignedRows: any[];
        /** Link row id per picked record, so detaching knows what to delete. */
        linkIdByTarget: Record<string, any>;
    } | null>(null);

    const handleOneToManyAssign = async (column: any, parentRow: any) => {
        const shape = resolveAssignShape(column);
        if (!shape || parentRow?.id === undefined) return;

        // The link's context fields are read from the published filters when the
        // payload is built, and the picker screen may compute its own columns
        // from them too (e.g. "is the vehicle free?" needs the plan's date
        // range), so publish this row's context first - same as a row click.
        applyRowContextFilters(screen, parentRow, setFilter, removeFilter);

        await openAssignPicker(shape, parentRow.id);
    };

    // An update form embeds the link screen itself as this table, so the
    // multi-picker sits in its header instead of a parent cell; the parent is
    // the form's record. Same flag and form shape as the cell, read off the
    // embedded screen in place of a column's relatedScreen.
    const embeddedAssignShape = embedded && parentRecordId !== undefined
        ? resolveAssignShape({relatedScreen: screen})
        : null;

    const handleEmbeddedAssign = async () => {
        if (!embeddedAssignShape || parentRecordId === undefined) return;

        // The form's record is the parent: publish it under the link screen's
        // filter codes, which is what a row click on the parent screen would do.
        embeddedAssignShape.parentFilterCodes.forEach(code => setFilter(code, String(parentRecordId)));

        await openAssignPicker(embeddedAssignShape, parentRecordId);
    };

    // A comments screen embedded in a form: bubbles instead of rows and a
    // composer under them that posts through the screen's create form.
    const isComments = embedded && isCommentsScreen(screen);
    const commentBodyColumn = isComments ? screen.columns.find(c => commentPart(c) === 'body') : undefined;
    const commentForm = commentBodyColumn ? (screen.createForms || []).find(f => commentTextField(f, commentBodyColumn)) : undefined;

    const sendComment = async (data: Record<string, any>, form: any): Promise<boolean> => {
        try {
            const response = form.kind === 'dynamic'
                ? await apiService.createDynamicFormData(form.id, data)
                : await apiService.createFormData(form.id, data);
            if (!response.success) {
                showError(response.message || 'Komentár sa nepodarilo odoslať');
                return false;
            }
        } catch (error: any) {
            showError(error?.message || 'Komentár sa nepodarilo odoslať');
            return false;
        }
        invalidateAppScreens();
        return true;
    };

    const openAssignPicker = async (shape: AssignShape, parentId: any) => {
        // Which records are already linked cannot be read from the cell: the
        // aggregated child rows render their target as a display string, with
        // no id to match a candidate against. Ask the link screen instead,
        // where a many-to-one column still serialises as {id, …}. Without this
        // the picker opens with nothing ticked and the same record gets
        // attached twice.
        const filters: Record<string, any> = {};
        shape.parentFilterCodes.forEach(code => {
            filters[code] = String(parentId);
        });

        const assignedRows: any[] = [];
        const linkIdByTarget: Record<string, any> = {};
        try {
            const response = await apiService.getScreenData(shape.linkScreenId, filters, '', null, null, null, 1000);
            if (!response.success) throw new Error(response.message || 'load failed');
            const payload: any = response.data;
            const links = Array.isArray(payload?.data) ? payload.data : (Array.isArray(payload) ? payload : []);
            links.forEach((link: any) => {
                const target = link?.[shape.targetColumnCode];
                if (target?.id === undefined || target?.id === null) return;
                assignedRows.push(target);
                linkIdByTarget[String(target.id)] = link.id;
            });
        } catch (error: any) {
            showError(error?.message || 'Nepodarilo sa načítať existujúce priradenia');
            return;
        }

        setOneToManyAssignState({shape, assignedRows, linkIdByTarget});
    };

    const applyOneToManyAssign = async (state: NonNullable<typeof oneToManyAssignState>, added: any[], removed: any[]) => {
        const {shape, linkIdByTarget} = state;
        const failed: string[] = [];

        // The parent (and any other context the form declares) comes from the
        // published filters, exactly as it would when the form is filled in by
        // hand - hand-assembling keys would break forms whose field codes differ.
        const contextData = buildContextFields(shape.createForm.fields, getFilter);

        // A table-bound form (DynamicForm) is created through its own endpoint,
        // the same split CreateSimpleFormPage makes.
        const createLink = shape.createForm.kind === 'dynamic'
            ? (data: Record<string, any>) => apiService.createDynamicFormData(shape.createForm.id, data)
            : (data: Record<string, any>) => apiService.createFormData(shape.createForm.id, data);

        for (const row of added) {
            if (row?.id === undefined) continue;
            try {
                const response = await createLink({
                    ...contextData,
                    [shape.targetFieldCode]: row.id,
                });
                if (!response.success) failed.push(response.message || `#${row.id}`);
            } catch (error: any) {
                failed.push(error?.message || `#${row.id}`);
            }
        }

        // Detaching must never destroy the picked record: only the link row goes.
        for (const row of removed) {
            if (row?.id === undefined) continue;
            const linkId = linkIdByTarget[String(row.id)];
            if (linkId === undefined) continue;
            try {
                await apiService.deleteScreenData(shape.linkScreenId, linkId);
            } catch (error: any) {
                failed.push(error?.message || `#${row.id}`);
            }
        }

        invalidateAppScreens();

        if (failed.length > 0) {
            showError(`Nepodarilo sa dokončiť ${failed.length} operácií: ${failed.join(', ')}`);
            return;
        }

        const parts: string[] = [];
        if (added.length > 0) parts.push(`priradených: ${added.length}`);
        if (removed.length > 0) parts.push(`odobraných: ${removed.length}`);
        showSuccess(`Hotovo — ${parts.join(', ')}`);
    };

    const handleOneToManyAssignSelect = async (added: any[], removed: any[]) => {
        if (!oneToManyAssignState || (added.length === 0 && removed.length === 0)) return;
        const state = oneToManyAssignState;
        setOneToManyAssignState(null);

        await applyOneToManyAssign(state, added, removed);
    };

    const [oneToManyAddState, setOneToManyAddState] = useState<{ form: any; parentId: any; contextCodes: string[] } | null>(null);
    // "Nothing found" offers to create the record from the searched text.
    const [searchCreateState, setSearchCreateState] = useState<{ form: any; prefill: Record<string, any>; lockedFields: string[] } | null>(null);
    const searchCreateForms: any[] = editable && !selectionMode ? (screen.createForms ?? []) : [];

    const openCreateFromSearch = (form: any) => {
        const prefill = searchPrefill(form.fields, search);
        const hasOneToMany = form.fields?.some((field: any) => field.type === 'one-to-many');
        if (hasOneToMany) {
            onOpenCreateForm?.(form, prefill ?? undefined);
            return;
        }
        setSearchCreateState({
            form,
            prefill: prefill ?? {},
            lockedFields: prefill ? Object.keys(prefill) : [],
        });
    };
    const urlAppId = routeAppId;

    const handleOneToManyAdd = (column: any, parentRow: any) => {
        const relatedScreen = column.relatedScreen;
        if (!relatedScreen || !relatedScreen.createForms?.length) return;
        const form = relatedScreen.createForms[0];
        const contextCodes = (relatedScreen.filters || []).map((f: any) => f.code);

        // Publish this row's whole context first - same as clicking the row.
        // The create form's own fields are not the only consumers: a picker
        // opened from inside it can compute columns from the parent's context
        // (e.g. "is the vehicle free?" needs the plan's date range), and a
        // screen whose SQL columns reference those filters fails outright
        // without them.
        applyRowContextFilters(screen, parentRow, setFilter, removeFilter);

        // Set context filters so the FK field gets pre-filled with parent row id
        contextCodes.forEach((code: string) => setFilter(code, String(parentRow.id)));

        setOneToManyAddState({ form, parentId: parentRow.id, contextCodes });
    };

    const closeOneToManyAddModal = () => {
        if (oneToManyAddState) {
            oneToManyAddState.contextCodes.forEach((code: string) => removeFilter(code));
        }
        setOneToManyAddState(null);
    };

    const setRowContextFilters = (row: any) => {
        applyRowContextFilters(screen, row, setFilter, removeFilter);
    };

    const handleRowClick = (row: any, e?: React.MouseEvent) => {
        if (e && (e.target as HTMLElement).closest('.delete-icon, .btn-icon')) {
            return;
        }

        const selection = window.getSelection();
        if (selection && selection.toString().length > 0) {
            return;
        }

        if (selectionMode && onRowClick) {
            onRowClick(row);
            return;
        }

        // Set context filters (shared for both redirect and action paths)
        setRowContextFilters(row);

        // Path 1: redirectScreenId - navigate directly
        if (screen.redirectScreenId) {
            const titleColumn = screen.columns.find(isTitleColumn);
            const rawTitleValue = titleColumn ? row[titleColumn.code] : undefined;
            const titleValue = rawTitleValue && typeof rawTitleValue === 'object'
                ? rawTitleValue[titleColumn?.targetColumn?.columnName ?? 'name'] ?? ''
                : rawTitleValue;
            if (onNavigateToScreen) {
                onNavigateToScreen(screen.redirectScreenId, titleValue ? String(titleValue) : undefined);
            }
            return;
        }

        // Path 2: action steps (existing logic)
        const clickAction = screen.actions?.find(action => action.type === 'click');

        if (!clickAction || !clickAction.steps) {
            // Path 3: fallback — if no click action and no redirect, but updateForm exists, open it
            if (screen.updateForms && screen.updateForms.length > 0 && onOpenUpdateForm) {
                onOpenUpdateForm(screen.updateForms[0], row);
            }
            return;
        }

        const sortedSteps = [...clickAction.steps].sort((a, b) => a.sortOrder - b.sortOrder);

        // Capture current filter values before context steps overwrite them
        const previousFilters: Record<string, string> = {};
        if (screen.filters) {
            screen.filters.forEach(f => {
                const value = getFilter(f.code);
                if (value) previousFilters[f.code] = value;
            });
        }

        sortedSteps.forEach(step => {
            if (step.type === 'context') {
                const contextKey = step.stepData?.value;
                const screenColumnId = step.stepData?.screenColumnId;

                if (contextKey) {
                    if (screenColumnId) {
                        const column = screen.columns.find(col => col.id === screenColumnId);
                        if (column) {
                            const columnValue = row[column.code];
                            if (columnValue !== undefined && columnValue !== null) {
                                const filterValue = typeof columnValue === 'object' && columnValue !== null && columnValue.id !== undefined
                                    ? String(columnValue.id)
                                    : String(columnValue);
                                setFilter(contextKey, filterValue);
                            }
                        }
                    } else if (row.id) {
                        const idValue = typeof row.id === 'object' && row.id !== null && row.id.id !== undefined
                            ? String(row.id.id)
                            : String(row.id);
                        setFilter(contextKey, idValue);
                    }
                }
            } else if (step.type === 'screen') {
                const screenId = step.stepData?.screenId;
                if (screenId && onNavigateToScreen) {
                    const titleColumn = screen.columns.find(isTitleColumn);
                    const rawTitleValue = titleColumn ? row[titleColumn.code] : undefined;
                    const titleValue = rawTitleValue && typeof rawTitleValue === 'object'
                        ? rawTitleValue[titleColumn?.targetColumn?.columnName ?? 'name'] ?? ''
                        : rawTitleValue;
                    onNavigateToScreen(screenId, titleValue ? String(titleValue) : undefined, previousFilters);
                }
            }
        });
    };

    const handleDeleteClick = (row: any, e: React.MouseEvent) => {
        e.stopPropagation();
        setRowToDelete(row);
        setShowConfirmDelete(true);
    };

    const handleConfirmDelete = async () => {
        if (!rowToDelete) return;

        setDeletingRowId(rowToDelete.id);

        const response = await deleteMutation.mutateAsync(rowToDelete.id);

        if (response.success) {
            invalidateAppScreens();
            // A DELETE workflow answers like a button workflow: its own notice
            // and, optionally, where the user should land afterwards.
            const responseData = response.data?.data || response.data;
            const notice = readWorkflowMessage(responseData);
            const redirect = readWorkflowRedirect(responseData);
            if (redirect) {
                applyWorkflowRedirect(redirect, {
                    app: currentApp,
                    appId: routeAppId!,
                    notice,
                    fallbackRecordId: null,
                    setFilter,
                    removeFilter,
                    navigate,
                });
            } else {
                showToast(notice?.text || 'Úspešne vymazané', notice?.type ?? 'success');
            }
        } else {
            showError(response.message || 'Chyba pri mazaní');
        }

        setDeletingRowId(null);
        setRowToDelete(null);
    };

    const currentQueryKey = ["screenData", screen.id, search, relevantFilterValues, effectiveUserFilterId, refreshTrigger, embedded ? "modal" : "normal", afterId, pageSize, sortColumnId, sortDir, JSON.stringify(appliedFilterGroup)];

    const handleBlur = () => {
        handleCellBlur(screen.id, currentQueryKey, visibleColumns);
    };

    const handleDirectCellUpdate = (row: any, column: any, value: any) => {
        handleDirectUpdate(screen.id, row, column, value, currentQueryKey);
    };

    const executeWorkflowAction = async (button: any, rowData: any) => {
        setWorkflowTitle(button.displayName);
        setWorkflowLoading(true);
        setWorkflowSuccess(undefined);
        setWorkflowMessage(undefined);
        setShowWorkflowModal(true);

        try {
            const response = await apiService.executeWorkflow(button.url, {data: rowData});

            if (response.success) {
                const responseData = response.data?.data || response.data;
                let fileData: FileData | null = null;

                if (responseData?.file && typeof responseData.file === 'object' && responseData.file.mimeType) {
                    fileData = responseData.file as FileData;
                } else if (responseData?.fileData && typeof responseData.fileData === 'object' && responseData.fileData.mimeType) {
                    fileData = responseData.fileData as FileData;
                } else if (responseData?.mimeType && (responseData?.data || responseData?.url)) {
                    fileData = responseData as FileData;
                }

                // A workflow can set its own notice by returning a 'message'
                // with a type; without one we fall back to a confirmation.
                const notice = fileData ? null : readWorkflowMessage(responseData);
                const successMessage = fileData
                    ? 'Súbor bol úspešne stiahnutý'
                    : notice?.text || 'Úspešne dokončené';
                if (fileData) {
                    await downloadFile(fileData);
                }

                // The workflow can also send the user somewhere else, with the
                // context it should be seen in. Not while a file is downloading.
                const redirect = fileData ? null : readWorkflowRedirect(responseData);
                if (redirect) {
                    applyWorkflowRedirect(redirect, {
                        app: currentApp,
                        appId: routeAppId!,
                        notice,
                        fallbackRecordId: rowData?.id,
                        setFilter,
                        removeFilter,
                        navigate,
                    });
                }

                // invalidate all screen tables in the current app — a workflow
                // can move records between sibling one-to-many tables in the same form
                invalidateAppScreens();
                setShowWorkflowModal(false);
                // The workflow decides how its notice reads: an 'error' notice
                // stays on screen until dismissed, the rest fade like any toast.
                if (!redirect) {
                    showToast(successMessage, notice?.type ?? 'success');
                }
                // keep isLoading=true so the modal shows the neutral spinner while it
                // animates closed — flipping it to false would render the error state
                setTimeout(() => {
                    setWorkflowLoading(false);
                    setWorkflowSuccess(undefined);
                    setWorkflowMessage(undefined);
                    setWorkflowTitle('');
                }, 300);
            } else {
                let errorMessage = response.message || 'Nastala chyba pri vykonávaní workflow';
                if (response.errors) {
                    if (Array.isArray(response.errors) && response.errors.length > 0) {
                        errorMessage = response.errors.join('\n');
                    } else if (typeof response.errors === 'object') {
                        const allErrors: string[] = [];
                        Object.values(response.errors).forEach((fieldErrors) => {
                            if (Array.isArray(fieldErrors)) {
                                allErrors.push(...fieldErrors);
                            }
                        });
                        if (allErrors.length > 0) {
                            errorMessage = allErrors.join('\n');
                        }
                    }
                }
                setWorkflowSuccess(false);
                setWorkflowMessage(errorMessage);
                setWorkflowLoading(false);
            }
        } catch (error) {
            setWorkflowSuccess(false);
            setWorkflowMessage('Nastala chyba pri vykonávaní workflow');
            setWorkflowLoading(false);
        }
    };

    const handleWorkflowButtonClick = async (button: any, rowData: any, e: React.MouseEvent) => {
        e.stopPropagation();

        if (button.confirm) {
            setConfirmConfig({
                ...button.confirm,
                onConfirm: () => executeWorkflowAction(button, rowData),
            });
            setShowConfirmModal(true);
        } else {
            await executeWorkflowAction(button, rowData);
        }
    };

    const handleWorkflowModalClose = () => {
        setShowWorkflowModal(false);
        setTimeout(() => {
            setWorkflowLoading(false);
            setWorkflowSuccess(undefined);
            setWorkflowMessage(undefined);
            setWorkflowTitle('');
        }, 300);
    };

    const handleUpdateClick = (row: any, form: any, e: React.MouseEvent) => {
        e.stopPropagation();
        setRowContextFilters(row);
        setShowUpdateDropdown(null);
        if (onOpenUpdateForm) {
            onOpenUpdateForm(form, row);
        }
    };

    const handlePageSizeChange = (size: number) => {
        setPageSize(size);
        updatePagination(null, []);
    };

    const handleFilterSelect = (filter: any) => {
        setSelectedUserFilter(filter.id);
        if (filter.conditions) {
            const normalized = normalizeConditions(filter.conditions);
            setFilterGroup(normalized);
            setOriginalFilterGroup(normalized);
            setAppliedFilterGroup(normalized);
            queryClient.invalidateQueries({queryKey: ["screenData", screen.id]});
        }
        setShowSavedFiltersDropdown(false);
    };

    const visibleColumns: ExpandedColumn[] = React.useMemo(() => {
        const nonHiddenColumns = screen.columns.filter(col => !col.screenHidden);
        // A bubble shows only what admin marked as a part of it.
        const shown = isComments ? nonHiddenColumns.filter(col => commentPart(col) !== null) : nonHiddenColumns;
        return expandColumns(shown);
    }, [screen.columns, isComments]);
    const hasClickAction = selectionMode
        || !!screen.redirectScreenId
        || screen.actions?.some(action => action.type === 'click')
        || (!!onOpenUpdateForm && !!screen.updateForms && screen.updateForms.length > 0);
    const hasSearchableColumns = hasSearchableColumn(screen.columns);
    const hasBarcodeSearchEnabled = hasBarcodeSearch(screen.columns);
    const filterableColumns = screen.columns.filter(col => col.filterable);

    // Map operators by column ID based on column type
    const operatorsByColumn = React.useMemo(() => {
        const result: Record<string, { value: string; label: string }[]> = {};
        filterableColumns.forEach(column => {
            const columnType = (column as any).columnType || 'text';
            const typeOperators = allOperators[columnType];

            if (typeOperators && typeOperators.operators) {
                result[column.id] = typeOperators.operators.map((op: any) => ({
                    value: op.value,
                    label: op.label
                }));
            }
        });
        return result;
    }, [allOperators, filterableColumns]);

    if (error) {
        return <div className="alert alert-danger">{(error as Error).message}</div>;
    }

    const handleCreateClick = (form: any) => {
        setShowCreateDropdown(false);
        if (onOpenCreateForm) {
            onOpenCreateForm(form);
        }
    };

    return (
        <>
            <ToastNotification
                toasts={toasts}
                onClose={hideToast}
            />

            {/* Controls row — OUTSIDE card, above the table (v5 layout) */}
            {!displayOnly && <ScreenDataTableHeader
                screen={screen}
                search={search}
                onSearchChange={setSearch}
                hasSearchableColumns={hasSearchableColumns}
                hasBarcodeSearch={hasBarcodeSearchEnabled}
                filterableColumnsCount={filterableColumns.length}
                showFilters={showFilters}
                onToggleFilters={() => setShowFilters(!showFilters)}
                savedFilters={savedFilters}
                selectedUserFilter={selectedUserFilter}
                showSavedFiltersDropdown={showSavedFiltersDropdown}
                onToggleSavedFiltersDropdown={() => setShowSavedFiltersDropdown(!showSavedFiltersDropdown)}
                onFilterSelect={handleFilterSelect}
                onClearFilter={() => {
                    setSelectedUserFilter(null);
                    setOriginalFilterGroup(null);
                    setAppliedFilterGroup(null);
                    setFilterGroup({type: 'group', operator: 'AND', conditions: []});
                    queryClient.invalidateQueries({queryKey: ["screenData", screen.id]});
                }}
                showCreateDropdown={showCreateDropdown}
                onToggleCreateDropdown={() => setShowCreateDropdown(!showCreateDropdown)}
                onCreateClick={handleCreateClick}
                onOpenCreateForm={onOpenCreateForm}
                onAssignExisting={embeddedAssignShape ? handleEmbeddedAssign : undefined}
                embedded={embedded}
                notifications={notifications}
                unreadCount={unreadCount}
                onDismissNotification={dismissNotification}
                onClearAllNotifications={clearAll}
                onMarkAllAsRead={markAllAsRead}
                editable={editable}
                autoFocusSearch={selectionMode}
                onSearchSubmit={() => {
                    if (selectionMode && onRowClick && data.length === 1) {
                        onRowClick(data[0]);
                    }
                }}
                pageSize={pageSize}
                onPageSizeChange={handlePageSizeChange}
                pageSizeOptions={screen.pages?.map(p => ({limit: p.limit}))}
                embeddedTitle={embeddedTitle}
                screenButtons={(screen.buttons || []).filter((b: any) => b.type === 'screen')}
                onScreenButtonClick={(button: any, e: React.MouseEvent) => {
                    // In an update form's one-to-many: pass formId + parent record id.
                    // On the main table: pass only screenId.
                    const context = parentFormId
                        ? {formId: parentFormId, id: parentRecordId}
                        : {screenId: screen.id};
                    handleWorkflowButtonClick(button, context, e);
                }}
            />}

            {/* Filter card - separate from the table card */}
            {showFilters && (
                <div className="card mb-3">
                    <div className="card-body">
                        <ScreenDataTableFilters
                            screenId={screen.id}
                            filterableColumns={filterableColumns}
                            showFilters={showFilters}
                            setShowFilters={setShowFilters}
                            filterGroup={filterGroup}
                            setFilterGroup={setFilterGroup}
                            appliedFilterGroup={appliedFilterGroup}
                            setAppliedFilterGroup={setAppliedFilterGroup}
                            originalFilterGroup={originalFilterGroup}
                            setOriginalFilterGroup={setOriginalFilterGroup}
                            selectedUserFilter={selectedUserFilter}
                            setSelectedUserFilter={setSelectedUserFilter}
                            savedFilters={savedFilters}
                            operatorsByColumn={operatorsByColumn}
                            onSuccess={showSuccess}
                            onError={showError}
                            embedded={embedded}
                            pageSize={pageSize}
                            onPageSizeChange={handlePageSizeChange}
                        />
                    </div>
                </div>
            )}

            <div className={isComments ? 'card comments' : 'card'} style={{position: 'relative'}}>
                {/* Loading spinner */}
                {(isLoading || isFetching) && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10
                    }}>
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Načítavam...</span>
                        </div>
                    </div>
                )}

                {/* Card Body - Table. While the next search is loading, `data` is the previous
                    key's placeholder — an empty previous result must not read as "nothing found". */}
                {data.length === 0 && !isLoading && !isPlaceholderData ? (
                    <div className="card-body">
                        {search.trim() !== '' && searchCreateForms.length > 0 ? (
                            <div className="alert alert-info mb-0 d-flex flex-wrap align-items-center gap-2" data-e2e="search-empty">
                                <span className="me-auto">Pre „{search.trim()}“ sa nič nenašlo. Vytvoriť nový záznam:</span>
                                {searchCreateForms.map((form: any) => (
                                    <Button
                                        key={form.id}
                                        size="sm"
                                        variant="primary"
                                        className="btn-pill"
                                        data-e2e={`search-create:${form.id}`}
                                        onClick={() => openCreateFromSearch(form)}
                                    >
                                        {form.displayName}
                                    </Button>
                                ))}
                            </div>
                        ) : (
                            <div className="alert alert-info mb-0">Žiadne dáta</div>
                        )}
                    </div>
                ) : (
                    <>
                        <div className="table-responsive">
                            <Table className="table card-table table-vcenter datatable">
                                <colgroup>
                                    {visibleColumns.map((column) => (
                                        <col
                                            key={column.id}
                                            style={widths[column.id] ? {width: widths[column.id]} : undefined}
                                        />
                                    ))}
                                </colgroup>
                                <thead>
                                <tr>
                                    {visibleColumns.map((column) => {
                                        const sortKey = (column.dataPath && column.dataPath.length > 0 ? column.dataPath : [column.code]).join('.');
                                        const isActive = sortColumnId === sortKey;
                                        const headerUi = resolveAppearance(column.appearance, 'column');
                                        return (
                                            <th
                                                data-id={column.id}
                                                data-code={column.code}
                                                key={column.id}
                                                data-type={column.columnType}
                                                data-context-key={column.contextCode}
                                                className={headerUi.headerClassName || undefined}
                                                onClick={() => handleSort(sortKey)}
                                                style={{cursor: 'pointer', userSelect: 'none', position: 'relative', ...headerUi.headerStyle}}
                                            >
                                                <span className="d-inline-flex align-items-center gap-1">
                                                    {column.displayName}
                                                    {isActive && sortDir === 'ASC' && <IconArrowUp size={14} stroke={2} />}
                                                    {isActive && sortDir === 'DESC' && <IconArrowDown size={14} stroke={2} />}
                                                    {!isActive && <IconArrowsSort size={14} stroke={1.5} className="text-muted opacity-25" />}
                                                </span>
                                                <span
                                                    className="col-resize-handle"
                                                    onMouseDown={(e) => startResize(column.id, e.currentTarget.parentElement!.offsetWidth, e)}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            </th>
                                        );
                                    })}
                                    {((screen.updateForms && screen.updateForms.length > 0) || screen.deleteEnabled || (screen.buttons && screen.buttons.length > 0)) && (
                                        <th className="w-1">
                                            <div className="d-flex gap-1">
                                                {Object.keys(widths).length > 0 && (
                                                    <button
                                                        className="btn btn-icon btn-sm btn-ghost-secondary"
                                                        onClick={() => resetWidths()}
                                                        title="Obnoviť šírky stĺpcov"
                                                        style={{width: '28px', height: '28px', padding: 0}}
                                                    >
                                                        <IconColumnsOff stroke={2} size={16}/>
                                                    </button>
                                                )}
                                                <button
                                                    className="btn btn-icon btn-sm btn-ghost-secondary"
                                                    onClick={() => queryClient.invalidateQueries({queryKey: ["screenData", screen.id]})}
                                                    title="Obnoviť dáta"
                                                    style={{width: '28px', height: '28px', padding: 0}}
                                                >
                                                    <IconRefresh stroke={2} size={16}/>
                                                </button>
                                            </div>
                                        </th>
                                    )}
                                </tr>
                                </thead>
                                <tbody>
                                {data.map((row: any, rowIndex: number) => {
                                    const isDeleting = deletingRowId === row.id;
                                    const isHighlighted = highlightedRows.has(row.id);

                                    // Check if row was created by current user
                                    const createdByColumn = visibleColumns.find(col => col.columnType === 'created-by');
                                    const rawCreatedBy = createdByColumn ? row[createdByColumn.code] : null;
                                    // The author comes as {id, name} when the column shows a target column, as a bare id otherwise.
                                    const rowCreatedBy = rawCreatedBy && typeof rawCreatedBy === 'object' ? rawCreatedBy.id : rawCreatedBy;
                                    const isOwnRow = currentUserId && rowCreatedBy && String(rowCreatedBy) === String(currentUserId);

                                    const uiRow = resolveAppearance(row._appearance?.row, 'rule');

                                    const isLocked = !!lockedRowIds?.some(id => String(id) === String(row.id));
                                    const isPicked = !isLocked && !!selectedRowIds?.some(id => String(id) === String(row.id));

                                    const classNames = [
                                        hasClickAction && 'table-row-clickable',
                                        isHighlighted && 'table-row-highlighted',
                                        isPicked && 'row-picked',
                                        isLocked && 'row-locked',
                                        isOwnRow && 'own-row',
                                        uiRow.className
                                    ].filter(Boolean).join(' ');

                                    return (
                                        <tr
                                            // By id: a cell keeps its state (the spinner of a picked many-to-one)
                                            // with its row when the list is read again in another order.
                                            key={row.id ?? rowIndex}
                                            data-e2e={`row:${row.id}`}
                                            onClick={(e) => handleRowClick(row, e)}
                                            className={classNames}
                                            style={{
                                                ...(hasClickAction ? {cursor: 'pointer'} : {}),
                                                ...(isDeleting ? {opacity: 0.5, pointerEvents: 'none'} : {}),
                                                ...uiRow.style
                                            }}
                                        >
                                            {visibleColumns.map((column) => (
                                                <EditableTableCell
                                                    key={column.id}
                                                    row={row}
                                                    column={selectionMode ? { ...column, editable: false } : column}
                                                    isEditing={isEditing(row.id, column.id)}
                                                    editValue={editValue}
                                                    onEditValueChange={setEditValue}
                                                    onCellClick={handleCellClick}
                                                    onCellBlur={handleBlur}
                                                    onDirectUpdate={handleDirectCellUpdate}
                                                    onOneToManyDelete={handleOneToManyDelete}
                                                    onOneToManyAdd={handleOneToManyAdd}
                                                    onOneToManyAssign={handleOneToManyAssign}
                                                    onBeforeOpenSelectModal={setRowContextFilters}
                                                />
                                            ))}
                                            <ScreenDataTableActions
                                                screen={screen}
                                                row={row}
                                                isDeleting={isDeleting}
                                                showUpdateDropdown={showUpdateDropdown}
                                                setShowUpdateDropdown={setShowUpdateDropdown}
                                                onWorkflowClick={handleWorkflowButtonClick}
                                                onUpdateClick={handleUpdateClick}
                                                onDeleteClick={handleDeleteClick}
                                            />
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </Table>
                        </div>

                        {/* Card Footer - Pagination */}
                        {!displayOnly && <ScreenDataTablePagination
                            screenId={screen.id}
                            totalRecords={totalRecords}
                            pageSize={pageSize}
                            canGoPrev={canGoPrev}
                            canGoNext={canGoNext}
                            onPrevPage={handlePrevPage}
                            onNextPage={handleNextPage}
                            onPageSizeChange={handlePageSizeChange}
                            pageSizeOptions={screen.pages?.map(p => ({limit: p.limit}))}
                            pageIndex={afterIdHistory.length}
                            rowsOnPage={data.length}
                        />}
                    </>
                )}
            </div>

            {commentForm && commentBodyColumn && (
                <CommentComposer
                    form={commentForm}
                    bodyColumn={commentBodyColumn}
                    parentFilters={filters || {}}
                    getFilter={getFilter}
                    onSend={sendComment}
                />
            )}

            <ConfirmModal
                show={showConfirmDelete}
                onHide={() => {
                    setShowConfirmDelete(false);
                    setRowToDelete(null);
                }}
                onConfirm={handleConfirmDelete}
                title="Naozaj chcete vymazať?"
                message="Túto akciu nie je možné vrátiť späť."
                confirmText="Vymazať"
                cancelText="Zrušiť"
                variant="danger"
            />

            <WorkflowModal
                show={showWorkflowModal}
                onHide={handleWorkflowModalClose}
                title={workflowTitle}
                isLoading={workflowLoading}
                success={workflowSuccess}
                message={workflowMessage}
            />

            {oneToManyAssignState && (
                <SelectModal
                    show={true}
                    onHide={() => setOneToManyAssignState(null)}
                    screenId={oneToManyAssignState.shape.pickerScreenId}
                    multiple={true}
                    onSelect={() => undefined}
                    onSelectMany={handleOneToManyAssignSelect}
                    confirmLabel="Uložiť"
                    assignedRows={oneToManyAssignState.assignedRows}
                    allowRemove={oneToManyAssignState.shape.canDetach}
                />
            )}

            {confirmConfig && (
                <ConfirmModal
                    show={showConfirmModal}
                    onHide={() => {
                        setShowConfirmModal(false);
                        setConfirmConfig(null);
                    }}
                    onConfirm={() => {
                        setShowConfirmModal(false);
                        confirmConfig.onConfirm();
                        setConfirmConfig(null);
                    }}
                    title={confirmConfig.title}
                    message={confirmConfig.body}
                    confirmText={confirmConfig.confirmButtonText}
                    variant={confirmConfig.variant || 'warning'}
                />
            )}

            <FormModal
                show={!!oneToManyAddState}
                onHide={closeOneToManyAddModal}
                form={oneToManyAddState?.form}
                appId={urlAppId || ''}
                onSuccess={() => {
                    invalidateAppScreens();
                    showSuccess('Záznam bol pridaný');
                }}
            />

            <FormModal
                show={!!searchCreateState}
                onHide={() => setSearchCreateState(null)}
                form={searchCreateState?.form}
                appId={urlAppId || ''}
                prefill={searchCreateState?.prefill}
                lockedFields={searchCreateState?.lockedFields}
                onSuccess={() => {
                    // The list re-runs the request it showed before, search included.
                    queryClient.invalidateQueries({queryKey: ['screenData', screen.id], refetchType: 'all'});
                    showSuccess('Záznam bol pridaný');
                }}
            />
        </>
    );
};
